import { FC, useEffect, useState } from 'react';
import type { AdminTenantListItem } from '@libs/types';
import { Box, Button, Dialog, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { BuildingsIcon, CheckIcon, XIcon } from '@phosphor-icons/react';

import { UpdateTenantDto, useUpdateTenant } from '../api/admin-api';

import { adminButtonSx, adminColors } from './adminTheme';

const SECTORS = [
  'Financial Services',
  'Banking',
  'Insurance',
  'Healthcare',
  'Education',
  'Government',
  'Technology',
  'Retail',
  'Transport & Logistics',
  'Energy & Utilities',
  'Telecom',
  'Other',
];

interface Props {
  open: boolean;
  onClose: () => void;
  tenant: AdminTenantListItem;
}

/** Modal that edits an existing tenant. The slug (`name`) is immutable. */
export const EditTenantModal: FC<Props> = observer(({ open, onClose, tenant }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const btn = adminButtonSx(theme);
  const { t } = useTrans();
  const update = useUpdateTenant(tenant.id);

  const [displayName, setDisplayName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [sector, setSector] = useState('');
  const [dpo, setDpo] = useState('');
  const [commercialRegistration, setCommercialRegistration] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Re-seed the form whenever a different tenant (or fresh data) is opened.
  useEffect(() => {
    if (!open) return;
    setDisplayName(tenant.displayName || '');
    setContactEmail(tenant.contactEmail || '');
    setSector(tenant.sector || '');
    setDpo(tenant.dpo || '');
    setCommercialRegistration(tenant.commercialRegistration || '');
    setSubmitted(false);
  }, [open, tenant]);

  const displayNameError = submitted && !displayName.trim();

  const handleSubmit = () => {
    setSubmitted(true);
    if (!displayName.trim()) return;
    const body: UpdateTenantDto = {
      displayName: displayName.trim(),
      contactEmail: contactEmail.trim() || undefined,
      sector: sector || undefined,
      dpo: dpo.trim() || undefined,
      commercialRegistration: commercialRegistration.trim() || undefined,
    };
    update.mutate(body, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 5, overflow: 'hidden' } }}>
      <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.625, borderBottom: `1px solid ${c.border}` }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2.5, backgroundColor: theme.palette.grey[200], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <BuildingsIcon size={20} color={c.orange} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: c.ink }}>
            {t('app.admin.editTenant.title')}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: c.faint, mt: 0.125 }}>
            {t('app.admin.editTenant.subtitle', { tenant: tenant.name })}
          </Typography>
        </Box>
        <Button onClick={onClose} sx={{ minWidth: 32, width: 32, height: 32, p: 0, borderRadius: 2, backgroundColor: theme.palette.grey[200], color: c.sub }}>
          <XIcon size={16} />
        </Button>
      </Box>

      <Box sx={{ px: 3, py: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label={t('app.admin.editTenant.slug')}
          value={tenant.name}
          disabled
          helperText={t('app.admin.editTenant.slugHint')}
          size="small"
          fullWidth
        />
        <TextField
          label={t('app.admin.editTenant.displayName')}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          error={displayNameError}
          helperText={displayNameError ? t('app.admin.editTenant.validation.required') : undefined}
          size="small"
          fullWidth
        />
        <TextField
          label={t('app.admin.editTenant.email')}
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          select
          label={t('app.admin.editTenant.sector')}
          value={sector}
          onChange={(e) => setSector(e.target.value)}
          size="small"
          fullWidth
        >
          {SECTORS.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('app.admin.editTenant.dpo')}
          value={dpo}
          onChange={(e) => setDpo(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label={t('app.admin.editTenant.commercialReg')}
          value={commercialRegistration}
          onChange={(e) => setCommercialRegistration(e.target.value)}
          size="small"
          fullWidth
        />
      </Box>

      <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.25, borderTop: `1px solid ${c.border}` }}>
        <Button onClick={onClose} sx={{ ...btn.neutral, px: 2, borderRadius: 2.75 }}>
          {t('app.admin.editTenant.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={update.isPending}
          startIcon={<CheckIcon size={16} />}
          sx={{ ...btn.solid, px: 2, borderRadius: 2.75 }}
        >
          {t('app.admin.editTenant.submit')}
        </Button>
      </Box>
    </Dialog>
  );
});
